import React, { useEffect, useMemo, useState } from 'react';
import { collect } from 'collect.js';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Button, Dropdown, Flex, Input, InputNumber, Table, Tag } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  MoreOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SyncOutlined,
  TruckOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { debounce } from 'lodash';
import {
  COUPON_TYPES,
  DeliveryProvider,
  FULFILLMENT_STATUSES,
  ORDER_METHODS,
  ORDER_PAYMENT_STATUSES,
  PaymentMethods,
  ROOM_SERVICE_LOCATION_TYPES,
} from '../../services/exports/Constants';
import OrderDetailsModal from '../../modals/order/OrderDetailsModal';
import useApiClient from '../../hooks/api/useApiClient';
import useHelpers from '../../hooks/context/useHelpers';
import useForm from '../../hooks/utility/useForm';
import CompanySelect from '../form/business/CompanySelect';
import DateRangePicker, { filterFactory, filter_today, variant_dropdown } from '../filters/DateRangePicker';
import ClickToCopy from '../ClickToCopy';
import OrderDeliveryDetailsModal from '../../modals/order/OrderDeliveryDetailsModal';
import { useUpdateEffect } from '../../hooks/utility/useUpdateEffect';
import TestRestaurantTag from '../provider-billing/TestRestaurantTag';

export default function OrdersTable({
  promoCodeId = null,
  parentCompanies = null,
  parentDateRange = null,
  hideCompaniesFilter = false,
  hideDateFilter = false,
  onMetaChange = null,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Sales:Orders' });

  const { OrdersManager } = useApiClient();
  const { formatCurrency, formatDateTime } = useHelpers();

  const { company } = useSelector((state) => state.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [showDeliveryDetailsModal, setShowDeliveryDetailsModal] = useState(false);
  const [receiptLoadingId, setReceiptLoadingId] = useState(null);

  const { data: filters, update: updateFilter } = useForm({
    companies: parentCompanies ?? (company?.id ? [company] : []),
    customer_details: null,
    amount_min: null,
    amount_max: null,
    ...(!hideDateFilter ? filterFactory(filter_today) : {}),
  });

  const effectiveCompanies = parentCompanies ?? filters.companies;
  const effectiveDateRange = parentDateRange ?? {
    start_date: filters.start_date,
    end_date: filters.end_date,
  };

  useEffect(() => {
    loadData(data?.current_page ?? 1);
  }, [
    serviceProvider?.id,
    effectiveCompanies,
    effectiveDateRange.start_date,
    effectiveDateRange.end_date,
    promoCodeId,
  ]);

  const debounced = debounce(async () => loadOrders(data?.current_page), 500);

  // Only on change: the mount request is already sent by the effect above.
  useUpdateEffect(() => {
    debounced();

    return () => debounced.cancel();
  }, [filters.customer_details, filters.amount_min, filters.amount_max]);

  async function loadData(page) {
    loadOrders(page);
  }

  async function loadOrders(page) {
    setLoading(true);
    const { data, success } = await OrdersManager.get({
      filter: {
        companies: collect(effectiveCompanies).pluck('id').toArray(),
        created_between:
          effectiveDateRange.start_date && effectiveDateRange.end_date
            ? [effectiveDateRange.start_date, effectiveDateRange.end_date]
            : undefined,
        customer_details: filters.customer_details,
        ...((filters.amount_min != null || filters.amount_max != null) && {
          amount: { min: filters.amount_min, max: filters.amount_max },
        }),
        ...(promoCodeId && { promo_code_id: promoCodeId }),
      },
      page,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    onMetaChange?.(data?.meta);

    return setData(data);
  }

  const columns = [
    {
      key: 'actions',
      fixed: 'left',
      render: (text, item) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'show_order_details',
                label: t('buttons.show_order_details'),
                onClick: () => toggleOrderDetailsModal(item),
                icon: <InfoCircleOutlined />,
              },
              ...(item.delivery_provider === DeliveryProvider.UberDrive && !!item.delivery?.id && !!item.scheduled_for
                ? [
                    {
                      key: 'update_dropoff_time',
                      label: t('buttons.update_dropoff_time'),
                      onClick: () => toggleDeliveryDetailsModal(item),
                      icon: <TruckOutlined />,
                    },
                  ]
                : []),
              {
                key: 'track_order',
                label: t('buttons.track_order'),
                onClick: () => window.open(`${company?.web_shop_url}/order-details/${item.uuid}`),
                icon: <ExportOutlined />,
              },
              // Only restaurants whose partner issues customer receipts; the backend rejects the rest.
              ...(item.can_download_receipt
                ? [
                    {
                      key: 'download_receipt',
                      label: t('buttons.download_receipt'),
                      onClick: () => downloadReceipt(item),
                      icon: receiptLoadingId === item.id ? <LoadingOutlined /> : <DownloadOutlined />,
                      disabled: receiptLoadingId === item.id,
                    },
                  ]
                : []),
              ...(item.payment_dashboard_url
                ? [
                    {
                      key: 'open_payment_page',
                      label: t('buttons.open_payment_page'),
                      onClick: () => window.open(item.payment_dashboard_url, '_blank'),
                      icon: <ExportOutlined />,
                    },
                  ]
                : []),
            ],
          }}
        >
          <MoreOutlined />
        </Dropdown>
      ),
    },
    {
      key: 'number',
      title: '#',
      dataIndex: ['fulfillment', 'number'],
      fixed: 'left',
    },
    {
      key: 'company',
      title: t('fields.company'),
      dataIndex: ['company', 'name'],
      render: (name, item) => (
        <>
          {name}
          <TestRestaurantTag company={item.company} className="ml-xs" />
        </>
      ),
    },
    {
      key: 'placed_at',
      title: t('fields.order_placed'),
      render: (_, item) => formatDateTime(item.created_at),
    },
    {
      key: 'ready_at',
      title: t('fields.ready_at'),
      render: (_, item) => formatDateTime(item.scheduled_for ?? item.created_at),
    },
    {
      key: 'fulfillment_status',
      title: t('fields.fulfillment_status'),
      render: (_, item) => {
        const color = {
          [FULFILLMENT_STATUSES.created]: 'warning',
          [FULFILLMENT_STATUSES.reviewed]: 'warning',
          [FULFILLMENT_STATUSES.accepted]: 'processing',
          [FULFILLMENT_STATUSES.preparing]: 'processing',
          [FULFILLMENT_STATUSES.on_delivery]: 'processing',
          [FULFILLMENT_STATUSES.ready]: 'processing',
          [FULFILLMENT_STATUSES.picked]: 'success',
          [FULFILLMENT_STATUSES.rejected]: 'error',
        }[item.fulfillment.status];

        const icon = {
          [FULFILLMENT_STATUSES.created]: <ClockCircleOutlined />,
          [FULFILLMENT_STATUSES.reviewed]: <ClockCircleOutlined />,
          [FULFILLMENT_STATUSES.accepted]: <SyncOutlined spin />,
          [FULFILLMENT_STATUSES.preparing]: <SyncOutlined spin />,
          [FULFILLMENT_STATUSES.on_delivery]: <SyncOutlined spin />,
          [FULFILLMENT_STATUSES.ready]: <SyncOutlined spin />,
          [FULFILLMENT_STATUSES.picked]: <CheckCircleOutlined />,
          [FULFILLMENT_STATUSES.rejected]: <CloseCircleOutlined />,
        }[item.fulfillment.status];

        return (
          <Flex gap="4px 0">
            <Tag color={color} icon={icon}>
              {t(
                `fulfillment_statuses.${item.method}.${item.fulfillment.status}`,
                t(`fulfillment_statuses.default.${item.fulfillment.status}`),
              )}
            </Tag>
            {item.fulfillment.status === FULFILLMENT_STATUSES.rejected && item.rejection_reason === 'auto' && (
              <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                {t('tags.rejected_automatically')}
              </Tag>
            )}
            {item.fulfillment.status === FULFILLMENT_STATUSES.rejected && item.rejection_reason !== 'auto' && (
              <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                {t('tags.rejected_manually')}
              </Tag>
            )}
          </Flex>
        );
      },
    },
    {
      key: 'total',
      title: t('fields.total'),
      render: (_, item) => formatCurrency(item.total, item.currency),
    },
    {
      key: 'customer',
      title: t('fields.customer'),
      render: (_, item) => `${item.customer_first_name ?? ''} ${item.customer_last_name ?? ''}`.trim(),
    },
    {
      key: 'items',
      title: t('fields.items'),
      render: (_, item) => collect(item.line_items).sum('quantity'),
    },
    {
      key: 'method',
      title: t('fields.method'),
      render: (_, item) =>
        t(
          item.method === ORDER_METHODS.room_service
            ? `order_methods.${item.method}.${item.room_service_location?.type}`
            : `order_methods.${item.method}`,
        ),
    },
    {
      key: 'tags',
      title: t('fields.tags'),
      render: (_, item) => (
        <Flex gap="4px 0">
          {item?.is_dummy && (
            <Tag color="error" icon={<ExclamationCircleOutlined />}>
              {t('tags.dummy')}
            </Tag>
          )}
          {item?.scheduled_for && (
            <Tag color="processing" icon={<ClockCircleOutlined />}>
              {t('tags.scheduled')}
            </Tag>
          )}
          {item?.coupon_type === COUPON_TYPES.REWARD && <Tag color="gold">{t('tags.reward')}</Tag>}
          {item?.payment?.payment_method_id === PaymentMethods.CASH && (
            <Tag color="default" icon={<ExclamationCircleOutlined />}>
              {t(item?.company?.overwrite_cash_payments_to_invoice ? 'tags.invoice' : 'tags.cash')}
            </Tag>
          )}
          {item?.method === ORDER_METHODS.pickup && <Tag color="green">{t('tags.pickup')}</Tag>}
          {item?.method === ORDER_METHODS.delivery && item?.delivery_provider === DeliveryProvider.Standalone && (
            <Tag color="orange">{t('tags.internal_delivery')}</Tag>
          )}
          {item?.method === ORDER_METHODS.delivery && item?.delivery_provider !== DeliveryProvider.Standalone && (
            <Tag color="lime">{t('tags.external_delivery')}</Tag>
          )}
          {item?.method === ORDER_METHODS.room_service &&
            item.room_service_location?.type === ROOM_SERVICE_LOCATION_TYPES.room_service && (
              <Tag color="volcano">{t('tags.room_service')}</Tag>
            )}
          {item?.method === ORDER_METHODS.room_service &&
            item.room_service_location?.type === ROOM_SERVICE_LOCATION_TYPES.table_service && (
              <Tag color="cyan">{t('tags.table_service')}</Tag>
            )}
          {item?.utm_source === 'foodamigos_occ' && (
            <Tag color="#108ee9" icon={<PhoneOutlined />}>
              {t('tags.call_center')}
            </Tag>
          )}
          {item.tags.map((tag) => (
            <Tag key={tag} color="geekblue">
              {t(`tags.${tag}`, { defaultValue: tag })}
            </Tag>
          ))}
        </Flex>
      ),
    },
    {
      key: 'payment_status',
      title: t('fields.payment_status'),
      render: (_, item) => {
        const getPaymentStatus = () => {
          if (item.payment_status === ORDER_PAYMENT_STATUSES.paid) {
            return ORDER_PAYMENT_STATUSES.paid;
          }

          if (item.payment_status === ORDER_PAYMENT_STATUSES.refunded) {
            return ORDER_PAYMENT_STATUSES.refunded;
          }

          return ORDER_PAYMENT_STATUSES.partially_refunded;
        };

        const color = {
          [ORDER_PAYMENT_STATUSES.paid]: 'success',
          [ORDER_PAYMENT_STATUSES.partially_refunded]: 'warning',
          [ORDER_PAYMENT_STATUSES.refunded]: 'error',
        }[getPaymentStatus()];

        const icon = {
          [ORDER_PAYMENT_STATUSES.paid]: <CheckCircleOutlined />,
          [ORDER_PAYMENT_STATUSES.partially_refunded]: <WarningOutlined />,
          [ORDER_PAYMENT_STATUSES.refunded]: <CloseCircleOutlined />,
        }[getPaymentStatus()];

        return (
          <Flex gap="4px 0">
            <Tag color={color} icon={icon}>
              {t(`payment_statuses.${getPaymentStatus()}`)}
            </Tag>
          </Flex>
        );
      },
    },
    {
      key: 'id',
      title: t('fields.id'),
      render: (_, item) => <ClickToCopy>{item.id}</ClickToCopy>,
    },
  ];

  function toggleOrderDetailsModal(order) {
    setSelectedOrder(order);
    setShowOrderDetailsModal(!showOrderDetailsModal);
  }

  async function downloadReceipt(order) {
    if (order.receipt_url) {
      window.open(order.receipt_url, '_blank');

      return;
    }

    setReceiptLoadingId(order.id);
    const { data, success } = await OrdersManager.generateReceipt(order.id);
    setReceiptLoadingId(null);

    if (!success || !data?.file_url) {
      toast.error(t('toasts.failed_to_download_receipt'));

      return;
    }

    window.open(data.file_url, '_blank');
  }

  const toggleDeliveryDetailsModal = (order) => {
    setSelectedOrder(order);
    setShowDeliveryDetailsModal(!showDeliveryDetailsModal);
  };

  const renderOrderDetailsModal = useMemo(
    () =>
      showOrderDetailsModal && (
        <OrderDetailsModal show={showOrderDetailsModal} toggleModal={toggleOrderDetailsModal} data={selectedOrder} />
      ),
    [selectedOrder, showOrderDetailsModal],
  );

  return (
    <>
      <div className="bg-background-inkWhite-white_0 shadow rounded-lg">
        <div className="flex justify-between items-center gap-4 p-md border-b border-brand-inkGrey-grey_2">
          <div className="lg:w-2/5">
            {!hideDateFilter && (
              <DateRangePicker
                defaultFilter={filter_today}
                variant={variant_dropdown}
                onChange={(value) => {
                  updateFilter('start_date', value.start_date);
                  updateFilter('end_date', value.end_date);
                }}
              />
            )}
            {!hideCompaniesFilter && (
              <CompanySelect
                id="companies"
                value={filters.companies}
                onChange={(value) => updateFilter('companies', value)}
                className="mt-md"
                isMulti
              />
            )}
            <Input.Search
              value={filters.customer_details}
              onChange={(e) => updateFilter('customer_details', e.target.value)}
              className="mt-md"
              placeholder={t('form.customer_details.placeholder')}
            />
            <Flex gap={8} className="mt-md">
              <InputNumber
                value={filters.amount_min}
                onChange={(value) => updateFilter('amount_min', value)}
                min={0}
                className="w-full"
                placeholder={t('form.amount_min.placeholder')}
              />
              <InputNumber
                value={filters.amount_max}
                onChange={(value) => updateFilter('amount_max', value)}
                min={0}
                className="w-full"
                placeholder={t('form.amount_max.placeholder')}
              />
            </Flex>
          </div>
          <div className="flex items-center justify-end">
            <Button size="large" icon={<ReloadOutlined />} onClick={() => loadData(data.current_page)} />
          </div>
        </div>
        <Table
          dataSource={data.data}
          columns={columns}
          loading={loading}
          className="rounded-none shadow-card bg-white overflow-hidden"
          rowKey={(item) => item.id}
          pagination={{
            current: data?.meta?.current_page,
            total: data?.meta?.total,
            pageSize: data?.meta?.per_page,
            onChange: (page) => loadData(page),
            rootClassName: 'overflow-hidden',
          }}
          scroll={{ x: 'max-content' }}
        />
      </div>
      {renderOrderDetailsModal}
      {showDeliveryDetailsModal && (
        <OrderDeliveryDetailsModal data={selectedOrder} show toggleModal={toggleDeliveryDetailsModal} />
      )}
    </>
  );
}
